import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApi } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { fmtEventDateTime, formatPrice } from "@/lib/format";
import { ChevronLeft, Mail } from "lucide-react";
import ComplimentaryCodesPanel from "@/components/ComplimentaryCodesPanel";

/** Prefer per-attendee names captured at checkout, then legacy flat
 *  attendee_names[], then the account holder as the last-resort label. */
function attendeesForBooking(b) {
  const fromAttendees = (b.attendees || []).map((a) => (a?.name || "").trim()).filter(Boolean);
  if (fromAttendees.length) return fromAttendees;
  const legacy = (b.attendee_names || []).map((n) => (n || "").trim()).filter(Boolean);
  if (legacy.length) return legacy;
  return b.user_name ? [b.user_name] : [];
}

/** Aggregate every non-cancelled booking's per-attendee menu picks into
 *  a per-course tally. Mirrors the mobile helper 1:1. */
function buildMenuTotals(event, bookings) {
  const cfg = event.menu_config;
  if (!cfg || cfg.mode === "none" || !cfg.mode) return null;
  const active = bookings.filter((b) => !((b.status || "").startsWith("cancelled")));
  const seed = (opts = []) => opts.map((o) => ({ option: o, count: 0 }));
  const bump = (arr, choice) => {
    if (!choice) return false;
    const row = arr.find((r) => r.option === choice);
    if (row) { row.count += 1; return true; }
    arr.push({ option: choice, count: 1 });
    return true;
  };
  let total = 0;
  if (cfg.mode === "simple") {
    const simple = seed(cfg.meal_options);
    let unspec = 0;
    active.forEach((b) => {
      const atts = b.attendees || [];
      const c = atts.length ? atts.length : (b.places || 0);
      total += c;
      if (atts.length === 0) { unspec += c; return; }
      atts.forEach((a) => { if (!bump(simple, a?.menu?.meal)) unspec += 1; });
    });
    return { mode: "simple", totalAttendees: total, simple, unspecified: { simple: unspec } };
  }
  const starter = cfg.starter_enabled ? seed(cfg.starter_options) : null;
  const main = seed(cfg.main_options);
  const dessert = cfg.dessert_enabled ? seed(cfg.dessert_options) : null;
  const unspec = { starter: 0, main: 0, dessert: 0 };
  active.forEach((b) => {
    const atts = b.attendees || [];
    total += atts.length ? atts.length : (b.places || 0);
    atts.forEach((a) => {
      if (starter && !bump(starter, a?.menu?.starter)) unspec.starter += 1;
      if (!bump(main, a?.menu?.main)) unspec.main += 1;
      if (dessert && !bump(dessert, a?.menu?.dessert)) unspec.dessert += 1;
    });
  });
  return {
    mode: "detailed", totalAttendees: total,
    starter, main, dessert,
    unspecified: {
      ...(starter ? { starter: unspec.starter } : {}),
      main: unspec.main,
      ...(dessert ? { dessert: unspec.dessert } : {}),
    },
  };
}

function Section({ title, children, testId }) {
  return (
    <Card className="p-5 mt-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">{title}</div>
      {children}
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 last:border-0 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value || "—"}</span>
    </div>
  );
}

function statusPill(s) {
  const st = (s || "").toLowerCase();
  const label = (s || "").replace(/_/g, " ") || "unknown";
  if (st === "confirmed") return { label, cls: "bg-emerald-50 text-emerald-800 border-emerald-300" };
  if (st === "pending_payment") return { label, cls: "bg-amber-50 text-amber-800 border-amber-300" };
  if (st.startsWith("cancelled") || st === "failed") return { label, cls: "bg-red-50 text-red-800 border-red-300" };
  return { label, cls: "bg-muted text-muted-foreground border-border" };
}

function MenuCourse({ heading, rows, unspecified, testId }) {
  return (
    <div className="mt-2" data-testid={testId}>
      <div className="text-xs font-semibold text-primary uppercase tracking-widest mb-1">{heading}</div>
      {(rows || []).length === 0 ? (
        <div className="text-sm text-muted-foreground italic">No choices recorded yet.</div>
      ) : (
        <ul className="text-sm space-y-1">
          {rows.map((r) => (
            <li key={r.option} className="flex items-center justify-between border-b border-border/60 py-1">
              <span>{r.option}</span>
              <span className="font-semibold">{r.count}</span>
            </li>
          ))}
          {unspecified > 0 && (
            <li className="flex items-center justify-between text-muted-foreground italic pt-1">
              <span>Not yet picked</span>
              <span>{unspecified}</span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export default function OrganiserEventPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const api = useApi();
  const [event, setEvent] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailing, setEmailing] = useState(false);

  const load = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const r = await api(`/organiser/events/${id}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d?.detail || "Could not load this event."); return; }
      setEvent(d.event); setBookings(d.bookings || []);
    } finally { setLoading(false); }
  }, [api, id]);
  useEffect(() => { load(); }, [load]);

  const { paidPlaces, complimentaryPlaces } = useMemo(() => {
    let paid = 0, comp = 0;
    for (const b of bookings) {
      if ((b.status || "").toLowerCase().startsWith("cancelled")) continue;
      const atts = b.attendees || [];
      if (atts.length === 0) { paid += b.places || 0; continue; }
      for (const a of atts) { if (a.is_complimentary) comp += 1; else paid += 1; }
    }
    return { paidPlaces: paid, complimentaryPlaces: comp };
  }, [bookings]);

  const totals = useMemo(() => (event ? buildMenuTotals(event, bookings) : null), [event, bookings]);

  const onEmailCsv = async () => {
    setEmailing(true);
    try {
      const r = await api(`/organiser/events/${id}/attendees/email-csv`, { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d?.detail || "Could not email the attendee CSV."); return; }
      if (d?.sent) toast.success(`CSV of ${d.row_count} attendee${d.row_count === 1 ? "" : "s"} emailed to ${d.to} (${d.filename}).`);
      else toast.error(`CSV prepared but SMTP delivery to ${d.to} failed.`);
    } finally { setEmailing(false); }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (error || !event) return (
    <div>
      <button onClick={() => nav("/organise")} className="text-sm text-primary inline-flex items-center gap-1"><ChevronLeft size={16}/> Back</button>
      <Card className="p-6 mt-4"><div className="font-semibold">Unavailable</div><p className="text-sm text-muted-foreground mt-1">{error || "Event not found."}</p></Card>
    </div>
  );

  const priceTxt = event.price_pence === 0 ? "Free" : formatPrice(event.price_pence, event.currency);
  const bookingsClosed = !!(event.bookings_close_at && new Date(event.bookings_close_at).getTime() <= Date.now());
  const customFields = event.custom_fields || [];

  return (
    <div>
      <button onClick={() => nav("/organise")} className="inline-flex items-center gap-1 text-sm text-primary mb-4" data-testid="organiser-back">
        <ChevronLeft size={16} /> Back
      </button>

      <Badge variant={bookingsClosed ? "destructive" : "secondary"}>
        {bookingsClosed ? "Bookings closed" : "Bookings open"}
      </Badge>
      <h1 className="text-2xl font-semibold mt-2">{event.name}</h1>
      <div className="text-sm text-muted-foreground mt-1">{fmtEventDateTime(event.starts_at)}</div>
      {event.location && <div className="text-sm text-muted-foreground">{event.location}</div>}

      <div className="grid gap-4 mt-4 md:grid-cols-2">
        <Section title="Overview" testId="section-overview">
          <Row label="Price" value={priceTxt} />
          <Row label="Capacity" value={String(event.capacity)} />
          <Row label="Places taken" value={String(event.places_taken)} />
          <Row label="Places remaining" value={String(event.places_remaining)} />
          <Row label="Paid places" value={String(paidPlaces)} />
          {event.complimentary_codes_enabled && (
            <Row label="Complimentary places" value={String(complimentaryPlaces)} />
          )}
          <Row label="Bookings" value={String(event.bookings_count ?? bookings.length)} />
          <Row label="Stripe account" value={event.stripe_account_label} />
          {event.bookings_close_at && (
            <Row label="Bookings close" value={fmtEventDateTime(event.bookings_close_at)} />
          )}
        </Section>

        {event.organiser && (
          <Section title="Organiser" testId="section-organiser">
            <Row label="Name" value={event.organiser} />
            {event.organiser_email && <Row label="Email" value={event.organiser_email} />}
          </Section>
        )}
      </div>

      <Section title="Attendee fields captured" testId="section-fields">
        {event.use_masonic_title && <Row label="Masonic title" value="Requested" />}
        {event.use_provincial_rank && <Row label="Provincial rank" value="Requested" />}
        {event.use_grand_rank && <Row label="Grand rank" value="Requested" />}
        {event.use_dietary_concerns && <Row label="Dietary concerns" value="Requested" />}
        {customFields.length > 0 ? (
          customFields.map((cf, i) => (
            <Row
              key={`${cf.name}-${i}`}
              label={cf.name}
              value={`${cf.scope === "attendee" ? "Per attendee" : "Per booking"}${cf.required ? " · required" : ""}`}
            />
          ))
        ) : (
          !event.use_masonic_title && !event.use_provincial_rank && !event.use_grand_rank && !event.use_dietary_concerns && (
            <div className="text-sm text-muted-foreground italic py-2">No custom fields defined for this event.</div>
          )
        )}
      </Section>

      <Section title={`Bookings (${bookings.length})`} testId="section-bookings">
        {bookings.length === 0 ? (
          <div className="text-sm text-muted-foreground italic py-2">No bookings yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {bookings.map((b) => {
              const atts = attendeesForBooking(b);
              const primary = atts[0] || b.user_name || "—";
              const extra = atts.slice(1);
              const pill = statusPill(b.status);
              return (
                <div key={b.booking_id} className="py-3 flex items-start justify-between gap-3" data-testid={`org-booking-${b.booking_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{primary}</div>
                    {extra.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-0.5">+ {extra.join(", ")}</div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      Booked by {b.user_name} · {b.user_email}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {b.places} place{b.places === 1 ? "" : "s"} · {fmtEventDateTime(b.created_at)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold text-sm">
                      {b.amount_pence === 0 ? "Free" : formatPrice(b.amount_pence, b.currency)}
                    </div>
                    <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full border capitalize ${pill.cls}`}>
                      {pill.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {totals && (
        <Section
          title={`Menu totals (${totals.totalAttendees} attendee${totals.totalAttendees === 1 ? "" : "s"})`}
          testId="section-menu-totals"
        >
          {totals.mode === "simple" ? (
            <MenuCourse
              heading="Meal choices"
              rows={totals.simple || []}
              unspecified={totals.unspecified?.simple || 0}
              testId="menu-totals-simple"
            />
          ) : (
            <>
              {totals.starter && (
                <MenuCourse
                  heading="Starter"
                  rows={totals.starter}
                  unspecified={totals.unspecified?.starter || 0}
                  testId="menu-totals-starter"
                />
              )}
              {totals.main && (
                <MenuCourse
                  heading="Main"
                  rows={totals.main}
                  unspecified={totals.unspecified?.main || 0}
                  testId="menu-totals-main"
                />
              )}
              {totals.dessert && (
                <MenuCourse
                  heading="Dessert"
                  rows={totals.dessert}
                  unspecified={totals.unspecified?.dessert || 0}
                  testId="menu-totals-dessert"
                />
              )}
            </>
          )}
        </Section>
      )}

      <div className="mt-6">
        <Button onClick={onEmailCsv} disabled={emailing} data-testid="organiser-email-attendee-csv">
          <Mail size={16} className="mr-2" />{emailing ? "Sending…" : "Email me the attendee CSV"}
        </Button>
        <p className="text-xs text-muted-foreground mt-2">
          One row per attendee — names, ranks, menu picks, dietary notes and every
          custom-field value captured on the booking. Cancelled bookings are excluded.
        </p>
      </div>

      {event.complimentary_codes_enabled && (
        <ComplimentaryCodesPanel eventId={event.event_id} mode="organiser" />
      )}
    </div>
  );
}
